import { get, isFunction, includes } from 'lodash';
import uuid from 'uuid';

export const CHANNEL_REQUEST = 'request';
export const CHANNEL_NOTIFICATION = 'notify';
export const CHANNEL_SUCCESS = 'success';
export const CHANNEL_ERROR = 'error';

const generateId = () => uuid.v4();

const channels = protocol => ({
  [CHANNEL_REQUEST]: (topic, data, meta) =>
    protocol[CHANNEL_REQUEST].publish(data.id, topic, data.params, meta),
  [CHANNEL_NOTIFICATION]: (topic, data, meta) =>
    protocol[CHANNEL_NOTIFICATION].publish(topic, data.params, meta),
  [CHANNEL_ERROR]: (topic, data, meta) =>
    protocol[CHANNEL_ERROR].publish(data.id, data.error, meta),
  [CHANNEL_SUCCESS]: (topic, data, meta) =>
    protocol[CHANNEL_SUCCESS].publish(data.id, data.result, meta),
});

const validateChannel = channel => includes([
  CHANNEL_REQUEST, CHANNEL_NOTIFICATION, CHANNEL_SUCCESS, CHANNEL_ERROR,
], channel);

const checkSubscribeParams = (channel, topic, handler) => {
  if (!validateChannel(channel)) {
    throw new Error(`Invalid channel name provided ${channel}`);
  }

  let result = { channel, topic, handler };

  if (isFunction(topic)) {
    result = { ...result, handler: topic, topic: '#' };
  }

  if (includes([CHANNEL_SUCCESS, CHANNEL_ERROR], channel)) {
    result.topic = channel;
  }

  return result;
};

const PROTOCOL = Symbol('protocol');
const QUEUE = Symbol('queue');
const SUBSCRIBE = Symbol('subscribe');
const ADD_SUBSCRIBER = Symbol('subscriber');
const SUBSCRIBERS_LIST = Symbol('subscribers-list');

export default class Dispatcher {
  constructor(protocol) {
    this[PROTOCOL] = protocol;
    this[QUEUE] = {};
    this[SUBSCRIBERS_LIST] = {};

    this.subscribe(CHANNEL_SUCCESS, (payload) => {
      if (!payload || !payload.id || !this[QUEUE][payload.id]) {
        return;
      }

      this[QUEUE][payload.id].resolve(payload);
      delete this[QUEUE][payload.id];
    });
    this.subscribe(CHANNEL_ERROR, (payload) => {
      if (!payload || !payload.id || !this[QUEUE][payload.id]) {
        return;
      }

      this[QUEUE][payload.id].reject(payload);
      delete this[QUEUE][payload.id];
    });
  }

  publish(channel, topic, { data, meta }) {
    if (!validateChannel(channel)) {
      throw new Error(`Invalid channel name provided ${channel}`);
    }
    channels(this[PROTOCOL])[channel](topic, data, meta);
  }

  request(topic, data, options = {}) {
    const id = generateId();
    const promise = new Promise((resolve, reject) => {
      this[QUEUE][id] = { resolve, reject };
    });

    this.publish(CHANNEL_REQUEST, topic, { data: { id, method: topic, params: data } });
    return Promise.race([promise, new Promise((res, rej) => {
      setTimeout(() => rej('Request timeout'), get(options, 'timeout', 3000));
    })]);
  }

  notify(topic, data) {
    this.publish(CHANNEL_NOTIFICATION, topic, { data: { method: topic, params: data } });
  }

  [ADD_SUBSCRIBER](subscriber) {
    if (isFunction(get(subscriber, 'unsubscribe'))) {
      const id = generateId();
      this[SUBSCRIBERS_LIST][id] = subscriber;
      return id;
    }
    return null;
  }

  [SUBSCRIBE](channel, topic, handler) {
    if (includes([CHANNEL_SUCCESS, CHANNEL_ERROR], channel)) {
      return this[ADD_SUBSCRIBER](this[PROTOCOL][channel].subscribe(handler));
    }

    // subscribe on request and notify channels
    return this[ADD_SUBSCRIBER](this[PROTOCOL][channel].subscribe(topic, (payload) => {
      if (payload && payload.method) {
        const promise = handler(payload, get(payload, 'meta', {}));
        if (promise && payload.id) {
          promise.then((result) => {
            this.publish(CHANNEL_SUCCESS, topic, { data: { id: payload.id, result } });
          }, (error) => {
            this.publish(CHANNEL_ERROR, topic, { data: { id: payload.id, error } });
          });
        }
      }
    }));
  }

  subscribe(channel, topic, handler) {
    const params = checkSubscribeParams(channel, topic, handler);
    return this[SUBSCRIBE](params.channel, params.topic, params.handler);
  }

  unsubscribe(subscriberId) {
    if (subscriberId && this[SUBSCRIBERS_LIST][subscriberId]) {
      this[SUBSCRIBERS_LIST][subscriberId].unsubscribe();
      delete this[SUBSCRIBERS_LIST][subscriberId];
    }
  }
}



// WEBPACK FOOTER //
// ./src/WebBridge/Dispatcher.js